
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Tag, TrendingUp, Eye, Star, RefreshCw } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { TablePagination } from '@/components/ui/table-pagination';
import { Skeleton } from '@/components/ui/skeleton';

interface TagAnalytics {
  tag: string;
  usageCount: number;
  effectivenessScore: number;
  avgRelevanceScore: number;
  documentsCount: number;
  lastUsed: string;
  trend: 'up' | 'down' | 'stable';
}

interface TagEffectiveness {
  tag: string;
  effectiveness: number;
  feedback_score: number;
  usage_frequency: number;
}

export const TagAnalyticsDashboard: React.FC = () => {
  const [tagAnalytics, setTagAnalytics] = useState<TagAnalytics[]>([]);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState<'usage' | 'effectiveness' | 'documents'>('usage');
  
  // Pagination states for tag table
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  
  // Chart display limit
  const [chartLimit, setChartLimit] = useState(10);

  useEffect(() => {
    loadTagAnalytics();
  }, []);

  const loadTagAnalytics = async () => {
    setLoading(true);
    try {
      // Get all documents with tags - only select existing columns
      const { data: documents, error: docsError } = await supabase
        .from('assistant_knowledge')
        .select('id, metadata, created_at');

      if (docsError) throw docsError;

      // Get knowledge effectiveness data
      const { data: effectiveness, error: effError } = await supabase
        .from('knowledge_effectiveness')
        .select('knowledge_document_id, relevance_score, was_used, created_at')
        .order('created_at', { ascending: false });

      if (effError) throw effError;

      // Process tag analytics
      const tagStats = new Map<string, TagAnalytics>();

      documents?.forEach(doc => {
        const tags = doc.metadata?.tags || [];
        tags.forEach((tag: string) => {
          if (!tagStats.has(tag)) {
            tagStats.set(tag, {
              tag,
              usageCount: 0,
              effectivenessScore: 0,
              avgRelevanceScore: 0,
              documentsCount: 0,
              lastUsed: doc.created_at,
              trend: 'stable' as const
            });
          }
          
          const current = tagStats.get(tag)!;
          current.documentsCount += 1;
          
          // Use created_at for lastUsed comparison
          if (new Date(doc.created_at) > new Date(current.lastUsed)) {
            current.lastUsed = doc.created_at;
          }
        });
      });

      // Calculate effectiveness scores from usage data
      effectiveness?.forEach(eff => {
        const doc = documents?.find(d => d.id === eff.knowledge_document_id);
        if (doc?.metadata?.tags) {
          doc.metadata.tags.forEach((tag: string) => {
            const tagStat = tagStats.get(tag);
            if (tagStat) {
              tagStat.usageCount += 1;
              tagStat.avgRelevanceScore = 
                (tagStat.avgRelevanceScore * (tagStat.usageCount - 1) + eff.relevance_score) / tagStat.usageCount;
              
              if (eff.was_used) {
                tagStat.effectivenessScore += 1;
              }
            }
          });
        }
      });

      // Calculate final effectiveness scores and trends with proper typing
      const analytics: TagAnalytics[] = Array.from(tagStats.values()).map(stat => ({
        ...stat,
        effectivenessScore: stat.usageCount > 0 ? (stat.effectivenessScore / stat.usageCount) * 100 : 0,
        trend: (stat.usageCount > 5 ? 'up' : stat.usageCount > 2 ? 'stable' : 'down') as 'up' | 'down' | 'stable'
      }));

      setTagAnalytics(analytics);
    } catch (error) {
      console.error('Error loading tag analytics:', error);
      toast.error('Failed to load tag analytics');
    } finally {
      setLoading(false);
    }
  };

  const sortedAnalytics = [...tagAnalytics].sort((a, b) => {
    switch (sortBy) {
      case 'usage':
        return b.usageCount - a.usageCount;
      case 'effectiveness':
        return b.effectivenessScore - a.effectivenessScore;
      case 'documents':
        return b.documentsCount - a.documentsCount;
      default:
        return 0;
    }
  });

  // Paginated data for the table
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedAnalytics = sortedAnalytics.slice(startIndex, endIndex);

  const chartData = sortedAnalytics.slice(0, chartLimit).map(stat => ({
    tag: stat.tag,
    usage: stat.usageCount,
    effectiveness: Math.round(stat.effectivenessScore),
    documents: stat.documentsCount
  }));

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const LoadingSkeleton = () => (
    <div className="space-y-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-center space-x-4">
          <Skeleton className="h-4 w-[100px]" />
          <Skeleton className="h-4 w-[80px]" />
          <Skeleton className="h-4 w-[60px]" />
          <Skeleton className="h-4 w-[120px]" />
          <Skeleton className="h-4 w-[80px]" />
          <Skeleton className="h-4 w-[60px]" />
          <Skeleton className="h-4 w-[100px]" />
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Tag Analytics Dashboard</h2>
        <Button onClick={loadTagAnalytics} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh Data
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Tags</CardTitle>
            <Tag className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{tagAnalytics.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Most Used Tag</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold truncate">
              {sortedAnalytics[0]?.tag || 'N/A'}
            </div>
            <p className="text-xs text-muted-foreground">
              {sortedAnalytics[0]?.usageCount || 0} uses
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Effectiveness</CardTitle>
            <Star className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {Math.round(tagAnalytics.reduce((sum, tag) => sum + tag.effectivenessScore, 0) / tagAnalytics.length || 0)}%
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Tags</CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {tagAnalytics.filter(tag => tag.usageCount > 0).length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Usage Chart */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Tag Performance Chart</CardTitle>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-muted-foreground">Show top:</span>
            <Select value={chartLimit.toString()} onValueChange={(value) => setChartLimit(parseInt(value))}>
              <SelectTrigger className="w-20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="h-[300px] flex items-center justify-center">
              <div className="text-center">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Loading chart data...</p>
              </div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="tag" 
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  interval={0}
                />
                <YAxis />
                <Tooltip />
                <Bar dataKey="usage" fill="#8884d8" name="Usage Count" />
                <Bar dataKey="effectiveness" fill="#82ca9d" name="Effectiveness %" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Detailed Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Tag Performance Details</CardTitle>
          <div className="flex space-x-2">
            <Button
              variant={sortBy === 'usage' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSortBy('usage')}
            >
              Sort by Usage
            </Button>
            <Button
              variant={sortBy === 'effectiveness' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSortBy('effectiveness')}
            >
              Sort by Effectiveness
            </Button>
            <Button
              variant={sortBy === 'documents' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSortBy('documents')}
            >
              Sort by Documents
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6">
              <LoadingSkeleton />
            </div>
          ) : (
            <>
              <div className="max-h-96 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tag</TableHead>
                      <TableHead>Documents</TableHead>
                      <TableHead>Usage Count</TableHead>
                      <TableHead>Effectiveness</TableHead>
                      <TableHead>Avg Relevance</TableHead>
                      <TableHead>Trend</TableHead>
                      <TableHead>Last Used</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedAnalytics.map((tag) => (
                      <TableRow key={tag.tag}>
                        <TableCell>
                          <Badge variant="secondary">{tag.tag}</Badge>
                        </TableCell>
                        <TableCell>{tag.documentsCount}</TableCell>
                        <TableCell>{tag.usageCount}</TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <Progress value={tag.effectivenessScore} className="w-16 h-2" />
                            <span className="text-sm">{Math.round(tag.effectivenessScore)}%</span>
                          </div>
                        </TableCell>
                        <TableCell>{tag.avgRelevanceScore.toFixed(2)}</TableCell>
                        <TableCell>
                          <Badge 
                            variant={tag.trend === 'up' ? 'default' : tag.trend === 'down' ? 'destructive' : 'secondary'}
                          >
                            {tag.trend}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(tag.lastUsed).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {/* Pagination */}
              <div className="border-t">
                <TablePagination
                  currentPage={currentPage}
                  pageSize={pageSize}
                  totalItems={sortedAnalytics.length}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  pageSizeOptions={[10, 25, 50, 100]}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
